import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Mail, Phone, Shield } from "lucide-react";
import { toast } from "sonner";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { Footer } from "@/components/layout/Footer";
import { api, getErrMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";

const ROLES = [
  { value: "family_office", label: "Family Office" },
  { value: "fund_manager", label: "Fund Manager / Allocator" },
  { value: "operator", label: "Operator deploying capital" },
  { value: "professional", label: "Investment Professional" },
  { value: "other", label: "Other" },
];

const CAPITAL_RANGES = [
  "< $1M",
  "$1M – $5M",
  "$5M – $25M",
  "$25M – $100M",
  "$100M – $500M",
  "$500M+",
];

const REGIONS = ["West Africa", "East Africa", "North Africa", "Southern Africa", "MENA", "Global"];

export default function Auth() {
  const [step, setStep] = useState(1); // 1: email+phone, 2: otp, 3: profile
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [sending, setSending] = useState(false);
  const [profileData, setProfileData] = useState({
    name: "",
    role: "",
    capital_deployed: "",
    regions: [],
  });

  const { setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromTier = location.state?.tier;

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email || !phone) return;
    setSending(true);
    try {
      const { data } = await api.post("/auth/request-otp", { email, phone });
      setDevOtp(data.dev_otp);
      toast.success("Code sent", { description: `Delivered to ${email} · Dev OTP: ${data.dev_otp}` });
      setStep(2);
    } catch (err) {
      toast.error("Could not send code", { description: getErrMsg(err) });
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async (code) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { email, code });
      await setToken(data.token);
      toast.success("Verified");
      if (data.new_user) setStep(3);
      else {
        if (fromTier && fromTier !== "institutional") {
          navigate("/pricing", { state: { tier: fromTier } });
        } else {
          navigate("/dashboard");
        }
      }
    } catch (err) {
      toast.error("Invalid code", { description: getErrMsg(err) });
    }
  };

  const handleProfile = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/auth/profile", profileData);
      toast.success("Profile completed");
      if (fromTier && fromTier !== "institutional") {
        navigate("/pricing", { state: { tier: fromTier } });
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error("Could not save", { description: getErrMsg(err) });
    }
  };

  const toggleRegion = (r) => {
    setProfileData((p) => ({
      ...p,
      regions: p.regions.includes(r) ? p.regions.filter((x) => x !== r) : [...p.regions, r],
    }));
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <PublicNavbar />

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md card-institutional p-8 md:p-10">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="h-5 w-5 gold-text" strokeWidth={1.5} />
            <span className="overline gold-text">Private Access</span>
          </div>

          {step === 1 && (
            <form onSubmit={handleSendOtp} className="space-y-5" data-testid="auth-form-step1">
              <div>
                <h1 className="font-display font-medium text-3xl tracking-tight">Request Access</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  Access is reviewed and granted selectively. Start with verification.
                </p>
              </div>

              <div>
                <label className="overline block mb-2">Email</label>
                <div className="flex items-center gap-2 border border-border rounded-md px-3 h-11 focus-within:border-[hsl(var(--gold))]">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@firm.com"
                    className="flex-1 bg-transparent outline-none text-sm"
                    data-testid="auth-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="overline block mb-2">Phone</label>
                <div className="flex items-center gap-2 border border-border rounded-md px-3 h-11 focus-within:border-[hsl(var(--gold))]">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <input
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 0000 000"
                    className="flex-1 bg-transparent outline-none text-sm"
                    data-testid="auth-phone-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending}
                className="btn-primary w-full"
                data-testid="auth-send-otp-button"
              >
                {sending ? "Sending…" : "Send verification code"} <ArrowRight className="h-4 w-4" />
              </button>

              <p className="text-xs text-muted-foreground text-center">
                Demo: try <span className="font-mono gold-text">admin@frontera.io</span>, <span className="font-mono gold-text">pro@frontera.io</span>, or <span className="font-mono gold-text">standard@frontera.io</span>
              </p>
            </form>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="auth-form-step2">
              <div>
                <h1 className="font-display font-medium text-3xl tracking-tight">Verify</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  Enter the 6-digit code sent to<br />
                  <span className="text-foreground font-mono">{email}</span>
                </p>
                {devOtp && (
                  <div className="mt-4 p-3 border border-[hsl(var(--gold))] rounded-md bg-[hsl(var(--gold-glow))]">
                    <div className="overline gold-text">Dev / Simulated OTP</div>
                    <div className="font-mono text-xl mt-1">{devOtp}</div>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(v) => {
                    setOtp(v);
                    if (v.length === 6) handleVerify(v);
                  }}
                  data-testid="auth-otp-input"
                >
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handleVerify(otp)}
                  disabled={otp.length !== 6}
                  className="btn-primary w-full"
                  data-testid="auth-verify-button"
                >
                  Verify
                </button>
                <button
                  onClick={() => setStep(1)}
                  className="text-sm text-muted-foreground hover:text-foreground"
                  data-testid="auth-back-button"
                >
                  ← Change details
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <form onSubmit={handleProfile} className="space-y-5" data-testid="auth-form-step3">
              <div>
                <h1 className="font-display font-medium text-3xl tracking-tight">Qualify</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  A few details to tailor your intelligence stream.
                </p>
              </div>

              <div>
                <label className="overline block mb-2">Full name</label>
                <input
                  type="text"
                  required
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <label className="overline block mb-2">Role</label>
                <select
                  required
                  value={profileData.role}
                  onChange={(e) => setProfileData({ ...profileData, role: e.target.value })}
                  className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                  data-testid="profile-role-select"
                >
                  <option value="">Select role</option>
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value} className="bg-[hsl(var(--background))]">{r.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="overline block mb-2">Capital deployed / year</label>
                <select
                  required
                  value={profileData.capital_deployed}
                  onChange={(e) => setProfileData({ ...profileData, capital_deployed: e.target.value })}
                  className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                  data-testid="profile-capital-select"
                >
                  <option value="">Select range</option>
                  {CAPITAL_RANGES.map((r) => (
                    <option key={r} value={r} className="bg-[hsl(var(--background))]">{r}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="overline block mb-2">Regions of interest</label>
                <div className="flex flex-wrap gap-2">
                  {REGIONS.map((r) => (
                    <button
                      type="button"
                      key={r}
                      onClick={() => toggleRegion(r)}
                      className={`px-3 py-1.5 text-xs border rounded-full transition-colors ${
                        profileData.regions.includes(r)
                          ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold-glow))] gold-text"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`region-pill-${r.replaceAll(" ", "-")}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              <button type="submit" className="btn-primary w-full" data-testid="profile-submit-button">
                Enter Frontera <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
